import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { SkeletonBase } from '@unified-checkout/design-system';

import type { IBalanceResponse } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance';
import type { IAccountBalanceStateProps } from '@app/dashboard/types/innerLayout/balance_and_transfers';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { useTrackOnce } from '@app/dashboard/hooks/useTrackOnce';
import { useLangStore } from '@app/dashboard/store/lang';
import { useCancelQuery } from '@app/dashboard/hooks/useCancelQuery';
import { getBalanceOverview } from '@app/dashboard/services/inner_layout/balance_and_transfers/balance';
import { MultiColorBar } from '@app/dashboard/components/inner_layout/MultiColorBar';
import { ComponentError } from '@app/dashboard/components/shared/ComponentError';

import { useActiveBalance } from '../../../hooks/useManageAccount';
import ManageAccounts from '../../../components/ManageAccounts';
import Header from './Header';
import Balances from './Balances';
import Reports from './Reports';

import styles from './overview.module.scss';

const Overview = ({
  activeBalance,
  setActiveBalance,
}: IAccountBalanceStateProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const { onChangeActiveBalance, haveAccountChanged, queryResults } =
    useActiveBalance({
      activeBalance,
      setActiveBalance,
    });

  const {
    data: currencies,
    isLoading: isBankAccountDetailsLoading,
    isFetching: isBankAccountDetailsFetching,
    isError: isBankAccountDetailsError,
  } = queryResults || {};

  const accountId = haveAccountChanged ?? activeBalance?.account?.id;

  const {
    data: balanceData,
    isFetching: isBalanceOverviewFetching,
    isError: isBalanceOverviewError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BALANCE_DETAILS, lang, accountId],
    queryFn: ({ signal }) => getBalanceOverview(accountId, signal),
    select: (response) => response?.data?.data as IBalanceResponse,
    enabled: !!accountId,
  });

  const isFetching = isBankAccountDetailsFetching || isBalanceOverviewFetching;
  const isError = isBankAccountDetailsError || isBalanceOverviewError;

  const {
    last_updated,
    available_balance,
    on_its_way,
    pending_balance,
    upcoming_pending,
  } = balanceData || {};

  useCancelQuery({
    queryKey: [QUERY_KEYS.GET_BALANCE_DETAILS, lang, accountId],
    deps: [lang, accountId],
  });

  useTrackOnce({
    trackFn: () => {
      const defaultCurrency = activeBalance?.currency;
      const pendingAmount = balanceData?.pending_balance?.amount || 0;
      const onItsWayAmount = balanceData?.on_its_way?.amount || 0;
      const availableAmount = balanceData?.available_balance?.amount || 0;
      const totalAmount = pendingAmount + onItsWayAmount + availableAmount;

      amplitude.track(BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.VIEW_BALANCE_PAGE, {
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.DEFAULT_CURRENCY]:
          defaultCurrency,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.ON_ITS_WAY_AMOUNT]:
          balanceData?.on_its_way?.amount,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.AVAILABLE_AMOUNT]:
          balanceData?.available_balance?.amount,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.PENDING_AMOUNT]:
          balanceData?.pending_balance?.amount,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.TOTAL_AMOUNT]:
          totalAmount,
      });
    },
    shouldTrack: !isBalanceOverviewFetching && !!activeBalance,
    deps: [isBalanceOverviewFetching, activeBalance, balanceData],
  });

  return (
    <div className={styles['overview']}>
      <Header
        lastUpdated={last_updated}
        isLoading={isFetching}
        isError={isError}
      />
      <div className={styles['overview__distribution']}>
        {isBankAccountDetailsLoading ? (
          <SkeletonBase width="100%" height="2.5rem" />
        ) : (
          !isBankAccountDetailsError && (
            <ManageAccounts
              isLoading={isFetching}
              currencies={currencies}
              activeBalance={activeBalance}
              onChangeActiveBalance={onChangeActiveBalance}
            />
          )
        )}
        <div className={styles['overview__distribution-details']}>
          {isError ? (
            <ComponentError
              text={t('innerLayout.errors.unableToRetrieveData')}
            />
          ) : (
            <div className={styles['overview__distribution-details-balance']}>
              {isFetching ? (
                <SkeletonBase width="100%" height=".5rem" />
              ) : (
                <MultiColorBar
                  segments={[
                    { value: pending_balance?.amount, color: '#F5A623' },
                    { value: on_its_way?.amount, color: '#4A90D9' },
                    { value: available_balance?.amount, color: '#27AE60' },
                  ]}
                />
              )}
              <Balances
                availableBalance={available_balance}
                pendingBalance={pending_balance}
                onItsWayBalance={on_its_way}
                upcomingBalance={upcoming_pending}
                isLoading={isFetching}
                currency={activeBalance?.currency || ''}
              />
            </div>
          )}
          <Reports activeBalance={activeBalance} />
        </div>
      </div>
    </div>
  );
};

export default Overview;
